import {getAllowedLayoutId} from "libs/layoutOptions";
import {codeLayoutOptions, pageLayoutOffer} from "libs/layoutOffer";
import {useGridAccess} from "../../grid/access";
import {ProjectsOverlay} from "../../pages/Projects";
import {GridModeNavigation} from "../../grid/ui/GridModeNavigation";
import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {Input} from "@rescui/input";
import cn from "classnames";
import "./index.css";
import {Select} from "@rescui/select";
import Button from "@rescui/button";
import {Toggle} from "@rescui/toggle";
import {useTextStyles} from "@rescui/typography";
import {buildUrl} from "utils/url";
import {
  CHANNELS,
  PRODUCTS,
  findChannelObj,
  productCodesForSelector,
} from "../../data/products.js";
import {allowsPixelation, variantsForLayout, variantsForProduct} from "../../data/products/index.js";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useSetting} from "../../contexts/setting.js";
import {useImages} from "../../contexts/images";
import {useBgTransformContext} from "../../contexts/bgTransform";
import BgSelector from "../BgSelector";
import GradientControls from "../GradientControls";
import {isLivestreamRasterProduct} from "pages/Livestream/productBackgrounds";
import PixelationControls from "../PixelationControls";
import LayoutSelector from "../LayoutSelector";
import TypografToggle from "./TypografToggle";
import LogoToggle, {LogoHiddenWarning} from "./LogoToggle";
import LinksPanel from "../LinksPanel";
import IconSelector from "../IconSelector";
import FontSelector from "../FontSelector";
import ScopeBar from "./ScopeBar";
import DataSelector from "../DataSelector";
import DefaultScenePanel from "../DefaultScenePanel";
import {DefaultsMenu} from "../DefaultScenePanel/Menu";
import {useChannelSettings} from "hooks/useChannelSettings";
import {applyStageOrder, productLandingStage, resolveStageCorrection} from "libs/stageOrder";
import {keepChoice} from "libs/heldSelection";
import {namesUrlBackground, writeUrlBackground} from "libs/bgNumbering";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {
  defaultLayoutId,
  hiddenBackgroundsOf,
  hiddenLayoutsOf,
  hiddenSetsOf,
  isBackgroundOffered,
  landingProduct,
  landingSetParam,
  layoutOrderOf,
  offeredLayoutOptions,
  offeredStages,
  orderedLayoutOptions,
  withoutHidden,
} from "libs/selectorVisibility";
import {offeredSelectableStages, productsNotOffered, stagesWithProduct} from "libs/productOffer";
import {homeChannel, homeType} from "data/offering";
import {resolveChannelLandingPath, resolveHomeLandingPath} from "libs/channelLanding";
import {
  InfoIcon,
  MessageIcon,
  ProjectsIcon,
  GearIcon,
  ExitIcon,
  DownIcon,
  UpIcon,
  HomeIcon,
} from "@rescui/icons";
import {API} from "../../api";
import {useAuth} from "hooks/useAuth";
import {
  PREPARE_PHOTO_LINK,
  DEFAULT_PRODUCT,
  DEFAULT_PRODUCTS_MAPPING,
  CONTENT_TYPES_WITHOUT_LAYOUTS,
  CHANNELS_WITHOUT_LAYOUTS,
  DEFAULT_TYPES,
  ALL_PRODUCT,
  UPLOAD_MAX_BYTES,
} from "data/constants";
import {defaultReleasesSetForProduct, releasesSetsForProduct} from "data/products/index";
import {getManualLink, getVisibleChannels, getFeedbackLink} from "utils/roles";
import {adminSectionsFor} from "data/permissions";
import {useFeatures} from "hooks/useFeature";
import {useCurrentSet} from "hooks/useCurrentSet";
import {track} from "libs/analytics";
import {fileToDataUrl, isSvgFile} from "libs/bg-utils";
import {openErrorDialog} from "layout/ErrorDialog";
import {isAirBanner} from "pages/Releases/AirBanner";

// Two letters for the avatar: the first of each of the first two
// dot/underscore/dash-separated chunks of the local part, so
// "sergey.golovachev@jetbrains.com" reads "SG". A single-chunk address
// ("admin@…") yields one letter rather than padding with a placeholder.
export function initialsFromEmail(email) {
  const local = String(email ?? "").split("@")[0];
  const chunks = local.split(/[._-]+/).filter(Boolean);
  if (!chunks.length) return "?";
  return (chunks[0][0] + (chunks[1]?.[0] ?? "")).toUpperCase();
}

const Toolbar = (props) => {
  const gridMode = useGridAccess();
  const [defaultsExpanded, setDefaultsExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    handleExport,
    handleSaveScene,
    buildScene,
    isWrongBrowser,
    copyButtonText,
    backgroundsData = {isPending: false, backgrounds: [], transforms: {}},
    bgDisplayOrder = null,
    linkGallery = null,
    isLinkGalleryPending = false,
    linkBackground = null,
    isChannelReady = true,
    isRouteDefaultReady = true,
  } = props;
  const {user, role, featureFlags, isLoading: authLoading} = useAuth();
  // Role features (data/features.ts). A toggle whose state is not the default
  // stays, so the role can turn it back.
  const features = useFeatures();
  const {isTypografEnabled, pixelationEnabled} = useSetting();
  const {logoHidden} = useImages();
  const {transformsReady} = useBgTransformContext();
  const adminSections = adminSectionsFor(role);
  const manualLink = getManualLink(role);
  const feedbackLink = getFeedbackLink(role);
  const handleLogout = () => {
    fetch("/auth/logout", {credentials: "include"}).then(() => {
      window.location.href = "/";
    });
  };
  const textCn = useTextStyles();
  const navigate = useNavigate();
  const {sceneId: templateId} = useParams();
  const {
    editLogoTransform,
    editLogos,
    bgBlurValue,
    editBgBlurValue,
    editUploadImages,
    removeUploadImages,
    uploadImages,
    removeImages,
    editUploadTransform,
    incPendingUpload,
    decPendingUpload,
  } = useImages();
  const {
    product,
    editBgOption,
    editProduct,
    channel,
    editChannel,
    type,
    editType,
    editLayoutOption,
    layoutOption,
    editVariantOption,
    bgOption,
    hash,
    isDropzoneVisible,
    makeDropzoneVisible,
    toggleUppercase,
    isUppercase,
    isExportLoading,
    fontOption,
    isFontToggle,
    editIsFontToggle,
    isButtonEnabled,
    editIsButtonEnabled,
    currentVideoPopup,
    clearSceneSet,
    titleFontSize,
    editTitleFontSize,
    dataChannel,
    dataType,
  } = useSetting();

  const activeSet = useCurrentSet();
  // The Set dropdown is per product: a product may carry a subset of the
  // channel's sets (JetBrains Air lists neither splash nor wallpapers), so both
  // the options and the "no ?set= in the URL" set follow the product.
  const productSets = useMemo(() => releasesSetsForProduct(product.value), [product.value]);
  const defaultSet = productSets[0];
  // The stage, set and layout the user wants, for the next Product switch
  // (libs/heldSelection.ts). A Channel switch and Home forget them.
  const heldChoicesRef = useRef({});

  // Channel-wide Stage option order, admin-saved. Null until loaded or when
  // nobody saved one — the declared order applies either way.
  const {data: channelSettings, isPending: isChannelSettingsPending} = useChannelSettings(
    channel.value
  );
  const stageOrder = channelSettings?.stageOrder ?? null;
  // What an admin hid from the dropdowns (libs/selectorVisibility.ts). The
  // Product dropdown is the entry channel's: a product hidden in the channel,
  // or on each of its stages, is not offered (libs/productOffer.ts). Sets
  // belong to the releases offering wherever a product of it is listed, and
  // so does their list: per channel, or per stage of the page.
  const {data: releasesSettings} = useChannelSettings("releases");
  const hiddenProducts = useMemo(
    () =>
      productsNotOffered(
        channelSettings,
        channel.value,
        [ALL_PRODUCT.value, ...PRODUCTS.map((p) => p.value)],
        type?.value
      ),
    [channelSettings, channel.value, type?.value]
  );
  const hiddenSets = useMemo(
    () => hiddenSetsOf(releasesSettings, dataType, product.value),
    [releasesSettings, dataType, product.value]
  );
  // The Set dropdown's options. The value is still read from `productSets`:
  // a link to a hidden set keeps it as the current value.
  const listedSets = useMemo(
    () => withoutHidden(productSets, hiddenSets),
    [productSets, hiddenSets]
  );

  // Sets, and the layout row each set draws, belong to the releases offering.
  // A product whose home is that channel keeps them wherever it is listed, so
  // this and every control below read `dataChannel` (src/data/offering.ts).
  const isSetsOffering = dataChannel === "releases";
  const activeSetOption = isSetsOffering
    ? productSets.find((s) => s.value === activeSet) || defaultSet
    : undefined;
  // A set that shows another channel's banners also shows that channel's
  // layout icons (the Emails set: the Email channel's).
  const layoutIconChannel = activeSetOption?.layoutIconChannel ?? dataChannel;
  // `layoutOptions` are the layouts the templates draw: a link may name any of
  // them. They are in the order an admin saved for the set and product
  // (Defaults panel, libs/selectorVisibility.ts): the first offered one is
  // the default layout. Stable across renders while inputs are unchanged —
  // the URL-sync effect below depends on it, so a fresh reference every
  // render would cause loops. The order is a dependency by its content: a
  // refetch of the settings gives a new array with the same ids.
  const {data: dataChannelSettings} = useChannelSettings(dataChannel);
  const layoutOrderIds =
    layoutOrderOf(dataChannelSettings, product.value, activeSet)?.join(",") ?? "";
  const layoutOrder = useMemo(
    () => (layoutOrderIds ? layoutOrderIds.split(",").map(Number) : null),
    [layoutOrderIds]
  );
  const layoutOptions = useMemo(
    () =>
      orderedLayoutOptions(
        codeLayoutOptions({dataChannel, type: dataType, set: activeSet, product: product.value}),
        layoutOrder
      ),
    [dataChannel, dataType, activeSet, product.value, layoutOrder]
  );
  // The layouts an admin took out of the Layout selector for this stage, set
  // and product (Defaults panel, libs/selectorVisibility.ts). The buttons
  // leave them out, but keep the current one: a link to a hidden layout still
  // shows which one is on.
  const hiddenLayouts = useMemo(
    () => hiddenLayoutsOf(dataChannelSettings, dataType, product.value, activeSet),
    [dataChannelSettings, dataType, product.value, activeSet]
  );
  const offeredLayouts = useMemo(
    () => offeredLayoutOptions(layoutOptions, hiddenLayouts, layoutOption),
    [layoutOptions, hiddenLayouts, layoutOption]
  );

  // const [isFontToggle, setIsFontToggle] = useState(searchParams.has("localization"));
  const [channelList, setChannelList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [iconAmount, setIconAmount] = useState(6);
  const [isCustomQrOpen, setIsCustomQrOpen] = useState(false);
  const eventLinksCount =
    channel.value === "events"
      ? Number(layoutOption) >= 1 && Number(layoutOption) <= 3
        ? Number(layoutOption)
        : 1
      : 1;

  // The screenshot the Air skin uploads is part of the design — its frames draw
  // the artwork, so there is nothing to hide and the toggle is not offered.
  // `AirBanner` renders that upload zone unconditionally (`alwaysVisible`), so
  // the flag another product left off cannot strand Air without a dropzone.
  // The skin follows the product, so every channel that lists it agrees.
  const isUploadLayout = [5, 6].includes(+layoutOption) && !isAirBanner(product.value);
  const isToggleOnSocial = channel.value === "social" && isUploadLayout;
  const isToggleOnInternal = channel.value === "internal" && isUploadLayout;
  const isToggleOnReleases = channel.value === "releases" && isUploadLayout;

  // const isVideoExportLoading = false;

  // URL → context sync moved to App.js (so it runs in render mode too).

  // URL → context sync for layout and background. Layout is validated
  // through getAllowedLayoutId — a URL param of ?layout=5 on a set whose
  // valid ids are [20..27] resolves to 20 instead of leaving the UI with
  // no highlighted button. Early-return when the param is absent rather
  // than calling editLayoutOption(layoutOption) — that was a no-op that
  // only served to make the effect re-fire on every state change.
  // The background that the URL names: a tile number is read once App knows
  // the tiles of the page (hooks/useBackgroundNumbers.ts).
  const {background: urlBackground} = useUrlBackground();
  useEffect(() => {
    const urlLayout = searchParams.get("layout");
    // Without a `?layout=` the page opens on the first offered layout, which
    // is also what getAllowedLayoutId falls back to — so the variant below has
    // a layout to be checked against either way, and the effect still depends
    // on the URL alone.
    const layoutId = urlLayout
      ? getAllowedLayoutId(urlLayout, layoutOptions)
      : (layoutOptions[0]?.id ?? 1);
    if (urlLayout) editLayoutOption(layoutId);
    if (urlBackground) editBgOption(urlBackground);
    // A variant the product does not declare falls back to the first one, the
    // way an out-of-range layout does above. The pair counts, not the variant
    // alone: a treatment the design draws for some layouts only is out of
    // range on the others (`variantsForLayout`).
    const urlVariant = Number(searchParams.get("variant"));
    if (urlVariant) {
      const declared = variantsForLayout(product.value, undefined, layoutId);
      const isDeclared = declared?.some((v) => v.id === urlVariant);
      editVariantOption(isDeclared ? urlVariant : (declared?.[0]?.id ?? 1));
    }
  }, [searchParams, urlBackground, layoutOptions, product.value]);

  // A page without `?layout=` opens on its default layout. Once the route
  // default has applied its layout, a hidden one gives way to the first
  // offered layout, the same as a click on it. When an admin saved a layout
  // order, the first offered layout of the order opens. A link (`?layout=`)
  // and a saved scene keep their layout.
  useEffect(() => {
    if (templateId || !isRouteDefaultReady) return;
    if (searchParams.get("layout")) return;
    if (!layoutOrder && !hiddenLayouts.has(layoutOption)) return;
    const next = defaultLayoutId(layoutOption, layoutOptions, hiddenLayouts, layoutOrder);
    if (next === layoutOption) return;
    setSearchParams(
      (params) => {
        params.set("layout", String(next));
        return params;
      },
      {replace: true}
    );
  }, [
    templateId,
    isRouteDefaultReady,
    searchParams,
    hiddenLayouts,
    layoutOrder,
    layoutOption,
    layoutOptions,
    setSearchParams,
  ]);

  // The same for the set: a page without `?set=` opens the product's first
  // set, and when an admin hid that set on this stage, the first visible set
  // opens instead, the same as a pick in the Set dropdown. A link (`?set=`)
  // and a saved scene keep their set.
  useEffect(() => {
    if (templateId || !isSetsOffering || !releasesSettings) return;
    if (product.value === ALL_PRODUCT.value || searchParams.get("set")) return;
    if (!activeSet || !hiddenSets.has(activeSet)) return;
    const next = listedSets[0]?.value;
    if (!next || next === activeSet) return;
    clearSceneSet();
    setSearchParams(
      (params) => {
        params.set("set", next);
        return params;
      },
      {replace: true, state: {resetOnArrival: true}}
    );
  }, [
    templateId,
    isSetsOffering,
    releasesSettings,
    product.value,
    searchParams,
    activeSet,
    hiddenSets,
    listedSets,
    clearSceneSet,
    setSearchParams,
  ]);

  const saveSceneTrigger = () => {
    handleSaveScene();
  };

  // TODO: Move channel visibility filtering to the server once constants are migrated to the database
  const updateChannelList = () => {
    const visibleChannels = getVisibleChannels(CHANNELS, role, featureFlags);
    const list = visibleChannels.map((ch) => ({label: ch.label, value: ch.value}));
    list.sort((a, b) => a.label.localeCompare(b.label));
    setChannelList(list);
  };
  const updateProductList = (ch = channel) => {
    // What the dropdown OFFERS. A content-type channel offers the current
    // type's products; a stage-typed channel (Releases) offers the whole
    // channel, so a product that ships on one stage only is still reachable.
    // productCodesForSelector owns that rule and the channel-switch fallback.
    const offeredProducts = productCodesForSelector(ch.value, type.value);

    const prodList = PRODUCTS.filter((p) =>
      offeredProducts.some((offered) => offered === p.value)
    );
    prodList.sort((a, b) => a.label.localeCompare(b.label));

    // Prepend "All Products" option for releases whats-new set
    if (ch.value === "releases" && activeSet === "whats-new") {
      prodList.unshift(ALL_PRODUCT);
    }

    // The dropdown leaves out what an admin hid. A hidden product stays valid:
    // a link to it opens it, and the dropdown shows it as the current value.
    const listedProducts = withoutHidden(prodList, hiddenProducts);
    setProductList(listedProducts);
    const findDefault = (list) =>
      list.find((productItem) => productItem.value === DEFAULT_PRODUCTS_MAPPING[ch.value]) ??
      list[0];
    const defaultProduct = findDefault(listedProducts) ?? findDefault(prodList) ?? DEFAULT_PRODUCT;

    const isCurrentProductValid = prodList.some((p) => p.value === product.value);

    if (!isCurrentProductValid) editProduct(defaultProduct);
  };

  // A stage-typed channel keeps the product and the stage independent, so a
  // route can name a stage the product does not carry — /releases/AIR/eap,
  // reachable by hand and by every in-app link that preserves the stage.
  // resolveStageCorrection decides; this only navigates.
  const stageCorrectionUrl = useMemo(() => {
    // An admin can put a product on a stage that the code does not give it
    // (libs/selectorVisibility.ts): until the settings load, such a route
    // looks like a stage the product does not carry.
    if (isChannelSettingsPending) return null;
    const channelObject = findChannelObj(channel.value);
    const correction = resolveStageCorrection({
      types: channelObject?.types,
      typesAreStages: channelObject?.typesAreStages,
      product: product.value,
      carriedTypes: stagesWithProduct(channelSettings, channel.value, product.value),
      type: type?.value,
      // The stage that a correction moves to is one that offers the product.
      selectableTypes: offeredSelectableStages(channelSettings, channel.value, product.value),
      stageOrder,
      savedSceneId: templateId,
      isAllProducts: product.value === ALL_PRODUCT.value,
    });
    if (!correction) return null;
    // The default layout of the stage the page moves to, on the product's
    // first set: the URL below names no set.
    const layout = pageLayoutOffer({
      dataChannel,
      type: homeType(correction.type, product.value),
      set: defaultSet?.value,
      product: product.value,
      settings: dataChannelSettings,
      declared: correction.layout,
    }).defaultId;
    // `background` is deliberately absent: the gallery's current first
    // background applies, the same as a bare page load (src/utils/url.js).
    return buildUrl(channel.value, product.value, correction.type, {layout});
  }, [
    templateId,
    channel.value,
    product.value,
    type?.value,
    stageOrder,
    channelSettings,
    isChannelSettingsPending,
    dataChannel,
    dataChannelSettings,
    defaultSet?.value,
  ]);

  useEffect(() => {
    if (!stageCorrectionUrl) return;
    // `replace`: a correction is not a step the user took, so Back must not
    // land on the stage that was just corrected away.
    navigate(stageCorrectionUrl, {replace: true, state: {resetOnArrival: true}});
  }, [stageCorrectionUrl, navigate]);

  const currentTypeValue = type?.value;
  const updateTypeList = useCallback(
    (channel, product) => {
      const channelObject = CHANNELS.find((ch) => ch.value === channel.value);

      if (!channelObject?.types?.length) {
        setTypeList([]);
      } else {
        // The stages the code gives the product, and the ones an admin put
        // it on.
        const withProduct = new Set(
          stagesWithProduct(channelSettings, channel.value, product.value, {selectable: true})
        );
        const carried = channelObject.types.filter((type) => withProduct.has(type.value));
        // A stage an admin hid the product on is not offered. The current
        // stage stays: a link to it shows which one is on.
        const offered = new Set(
          offeredStages(
            channelSettings,
            product.value,
            carried.map((t) => t.value),
            currentTypeValue
          )
        );
        const filteredTypes = carried.filter((t) => offered.has(t.value));

        const updatedTypeList = filteredTypes.map(({label, value, altLabel}) => ({
          label,
          value,
          ...(altLabel && {altLabel}),
        }));

        // The Stage dropdown follows the admin-saved channel-wide order
        // (which stage leads is seasonal — EAP first during an EAP cycle);
        // null keeps the declared products.js order.
        setTypeList(applyStageOrder(updatedTypeList, stageOrder));
      }
    },
    [stageOrder, channelSettings, currentTypeValue]
  );

  useEffect(() => {
    updateTypeList(channel, product);
  }, [channel, product, updateTypeList]);

  const onChangeChannel = async (selectedChannel) => {
    // Skip if selecting the same channel
    if (selectedChannel.value === channel.value) return;

    track("neurobaker.channel.selected", {channel: selectedChannel.value});
    heldChoicesRef.current = {};
    API.clearBackgroundCache();
    // Resolved the same way a bare /<channel> URL resolves — the landing stage
    // follows the admin-saved order. Navigating to the bare path instead would
    // lose the resetOnArrival state across the loader's redirect.
    navigate(await resolveChannelLandingPath(selectedChannel.value), {
      state: {resetOnArrival: true},
    });
  };

  // The index route opens the last location of the user (libs/lastLocation.ts),
  // so Home is the way back to the default landing. It resets the editor the
  // same way a channel switch does.
  const onGoHome = async () => {
    heldChoicesRef.current = {};
    API.clearBackgroundCache();
    navigate(`/${await resolveHomeLandingPath({role, featureFlags})}`, {
      state: {resetOnArrival: true},
    });
  };

  const onChangeProduct = (selectedProduct) => {
    // Skip if selecting the same product
    if (selectedProduct.value === product.value) return;

    track("neurobaker.product.selected", {product: selectedProduct.value, channel: channel.value});
    // Clear uploaded images and their transforms when changing product
    removeImages();
    editUploadTransform({}, {}, {}, {}, {});

    // "All Products" shortcut — navigate directly with set=whats-new
    if (selectedProduct.value === ALL_PRODUCT.value) {
      const allProductsType = type?.value || DEFAULT_TYPES[channel.value]?.value;
      navigate(
        buildUrl(
          channel.value,
          ALL_PRODUCT.value,
          allProductsType,
          // `background` is deliberately absent: see src/utils/url.js.
          {
            layout: pageLayoutOffer({
              dataChannel: "releases",
              type: allProductsType,
              set: "whats-new",
              product: ALL_PRODUCT.value,
              settings: releasesSettings,
              declared: 1,
            }).defaultId,
            set: "whats-new",
          }
        ),
        {state: {resetOnArrival: true}}
      );
      return;
    }

    // Each sub-selection stays when the new product offers it, and goes to the
    // product's default when not. The value the user wanted is remembered
    // and comes back on the next product that offers it (libs/heldSelection.ts).
    const remembered = heldChoicesRef.current;
    const channelObject = CHANNELS.find((ch) => ch.value === channel.value);
    const declaredStage = DEFAULT_TYPES[channel.value]?.value;
    // A stage the user chose stays, a non-selectable direct-route stage
    // included. On a stage-typed channel, the stage the held product opens on
    // is not a choice: JetBrains Air lists Release alone, and every product
    // picked after it opens on its own leading saved stage.
    // A stage an admin hid the product on is not offered, and is no landing
    // stage (libs/productOffer.ts).
    const isStageChosen =
      !channelObject?.typesAreStages ||
      type?.value !==
        productLandingStage(
          stageOrder,
          offeredSelectableStages(channelSettings, channel.value, product.value),
          declaredStage
        );
    const stage = keepChoice({
      held: type?.value,
      remembered: remembered.type,
      isHeldChosen: isStageChosen,
      offered: offeredStages(
        channelSettings,
        selectedProduct.value,
        stagesWithProduct(channelSettings, channel.value, selectedProduct.value)
      ),
      fallback: productLandingStage(
        stageOrder,
        offeredSelectableStages(channelSettings, channel.value, selectedProduct.value),
        declaredStage
      ),
    });
    const targetType = homeType(stage.value, selectedProduct.value);

    // Sets belong to the releases offering. A set hidden on the new stage is
    // not offered: the product opens on its first visible set there.
    const targetDataChannel = homeChannel(channel.value, selectedProduct.value);
    const targetSets =
      targetDataChannel === "releases" ? releasesSetsForProduct(selectedProduct.value) : [];
    const targetSetValues = targetSets.map((s) => s.value);
    const targetHiddenSets = hiddenSetsOf(releasesSettings, targetType, selectedProduct.value);
    const set = targetSets.length
      ? keepChoice({
          held: activeSet,
          remembered: remembered.set,
          offered: targetSetValues.filter((value) => !targetHiddenSets.has(value)),
          fallback: landingSetParam(targetSetValues, targetHiddenSets) ?? targetSetValues[0],
        })
      : null;

    // The layout buttons of the new product, from the same inputs as
    // `layoutOptions` above. A layout it does not draw, or one an admin hid
    // there, gives way to the default layout of the new page.
    const typeConfig = channelObject?.types?.find((t) => t.value === stage.value);
    const targetLayouts = pageLayoutOffer({
      dataChannel: targetDataChannel,
      type: targetType,
      set: set?.value,
      product: selectedProduct.value,
      // The target channel is the entry channel or releases: both are loaded.
      settings: targetDataChannel === "releases" ? releasesSettings : channelSettings,
      declared: typeConfig?.defaultLayout ?? 1,
    });
    const layout = keepChoice({
      held: getAllowedLayoutId(layoutOption, layoutOptions),
      remembered: remembered.layout,
      offered: targetLayouts.options
        .map((option) => option.id)
        .filter((id) => !targetLayouts.hidden.has(id)),
      fallback: targetLayouts.defaultId,
    });

    heldChoicesRef.current = {
      type: stage.remembered,
      set: set?.remembered,
      layout: layout.remembered,
    };

    // No `set` opens the product's first set.
    // `background` is deliberately absent: see src/utils/url.js.
    const navParams = {layout: layout.value};
    if (set && set.value !== targetSetValues[0]) navParams.set = set.value;

    // Every value must come STRICTLY from the new key's defaults, the way a
    // page load does. Without the full reset, fields the default scene does
    // not mention (brightness, uppercase, pixelation knobs, …) inherit the
    // previous product's state — apply is present-wins by design.
    navigate(buildUrl(channel.value, selectedProduct.value, stage.value, navParams), {
      state: {resetOnArrival: true},
    });
  };

  const onChangeType = (selectedType) => {
    // Skip if selecting the same type
    if (selectedType.value === type?.value) return;

    track("neurobaker.content_type.selected", {
      contentType: selectedType.value,
      channel: channel.value,
    });
    // Clear background cache when changing type
    API.clearBackgroundCache();
    // Clear uploaded images and their transforms when changing type
    removeImages();
    editUploadTransform({}, {}, {}, {}, {});

    // Get defaultLayout from the content type config if available
    const channelObject = CHANNELS.find((ch) => ch.value === channel.value);
    const typeConfig = channelObject?.types?.find((t) => t.value === selectedType.value);
    const targetType = homeType(selectedType.value, product.value);

    // The set stays when the new stage offers it. A set an admin hid there
    // gives way to the first visible set, and comes back on the next stage
    // that offers it (libs/heldSelection.ts).
    const setValues = productSets.map((s) => s.value);
    const targetHiddenSets = hiddenSetsOf(releasesSettings, targetType, product.value);
    const set = activeSet
      ? keepChoice({
          held: activeSet,
          remembered: heldChoicesRef.current.set,
          offered: setValues.filter((value) => !targetHiddenSets.has(value)),
          fallback: setValues.find((value) => !targetHiddenSets.has(value)) ?? activeSet,
        })
      : null;
    if (set) heldChoicesRef.current = {...heldChoicesRef.current, set: set.remembered};

    // The default layout of the new stage: the first offered one of the
    // admin's order, else the stage's own default when the templates draw it
    // and no admin hid it there.
    const defaultLayout = pageLayoutOffer({
      dataChannel,
      type: targetType,
      set: set?.value,
      product: product.value,
      settings: dataChannelSettings,
      declared: typeConfig?.defaultLayout ?? 1,
    }).defaultId;

    // `background` is deliberately absent: see src/utils/url.js.
    const navParams = {layout: defaultLayout};
    if (set && set.value !== defaultSet.value) {
      navParams.set = set.value;
    }

    // Same rule as the product switch above: the new stage renders exactly
    // what a fresh page load of it would.
    navigate(buildUrl(channel.value, product.value, selectedType.value, navParams), {
      state: {resetOnArrival: true},
    });
  };

  useEffect(() => {
    updateProductList(channel);
  }, [channel, type, product, activeSet, hiddenProducts]);

  useEffect(() => {
    updateChannelList();
    updateProductList(channel);
    // The same list as the effect above: the stages an admin put the product
    // on or hid it on count here too.
    updateTypeList(channel, product);
  }, [role, featureFlags]);

  const handleCustomQRUploadFor = (slotId) => async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // SVG: store inline as a data URL instead of uploading to S3 (the
    // /api/v3/uploads allowlist rejects image/svg+xml; SVG renders via
    // <img src>, sandboxed). Mirrors ImageUpload / BgSelector. No blob
    // preview / pendingUpload — the value is final once the read resolves.
    if (isSvgFile(file)) {
      try {
        editUploadImages(slotId, await fileToDataUrl(file), true);
      } catch (err) {
        console.error("[Toolbar/custom-qr] SVG read failed", err);
        openErrorDialog({
          title: "Couldn't load SVG",
          summary: "The SVG file couldn't be read. Try a different file.",
        });
      }
      return;
    }

    // Upload-first (#169). Optimistic local preview, then swap to the
    // CDN URL once the upload resolves. Save is blocked by the global
    // `pendingUploadCount` while in flight.
    const localUrl = URL.createObjectURL(file);
    const previousValue = uploadImages?.[slotId] || "";
    editUploadImages(slotId, localUrl, true);
    incPendingUpload();
    try {
      const result = await API.uploadFile(file);
      if (!result?.url) {
        throw new Error("Upload API returned no URL");
      }
      editUploadImages(slotId, result.url, true);
    } catch (err) {
      console.error("[Toolbar/custom-qr] upload failed", err);
      // Custom-QR slots are simpler than the BgSelector / ImageUpload flow:
      // there's no concurrent-pick scenario (one input per slot, gated by
      // the file dialog), so reading `previousValue` from this single
      // closure is safe — the function doesn't run twice for the same
      // slotId in overlapping invocations.
      editUploadImages(slotId, previousValue, true);
      openErrorDialog({
        title: "Couldn't upload QR image",
        summary:
          err?.envelope?.error?.isLikelyCors || err?.name === "NetworkError"
            ? "The upload didn't reach the server. Check your connection and try again."
            : err?.status === 413
              ? `The file is too large. Try a smaller one (under ${UPLOAD_MAX_BYTES / (1024 * 1024)} MB).`
              : err?.status === 400 || err?.status === 415
                ? "Unsupported file type."
                : `Upload failed${err?.message ? `: ${err.message}` : ""}.`,
        diagnostics: err?.envelope,
      });
    } finally {
      URL.revokeObjectURL(localUrl);
      decPendingUpload();
    }
  };

  // The second design axis, declared per product, and per set: a treatment
  // restyles the product's own banner, so a set that renders a shared template
  // instead has no treatment axis (`variantsForProduct`).
  const productVariants = variantsForProduct(product.value, activeSet);

  // One layout alone is no choice, so the panel hides itself. A product with
  // brand treatments still has a row to offer: the grid's columns.
  const showLayoutSelector =
    !CHANNELS_WITHOUT_LAYOUTS.includes(channel.value) &&
    !CONTENT_TYPES_WITHOUT_LAYOUTS.includes(type.value) &&
    (offeredLayouts.length > 1 || (productVariants && offeredLayouts.length > 0));
  // Releases carries version strings and stage names that Typograf's
  // smart-quote/dash rewriting only mangles — the control is hidden there
  // (the setting itself keeps its default).
  const showTypograf =
    dataChannel !== "releases" && (features["sidebar.typograf"] || isTypografEnabled);
  const showLogoToggle = dataChannel === "social" && (features["sidebar.logo-toggle"] || logoHidden);

  // The layout panel's footer holds the controls that shape the text and logo
  // on the banner. Null when it would be empty, so the panel keeps no rule
  // under an empty strip.
  const layoutPanelFooter =
    showTypograf || showLogoToggle ? (
      <>
        <div className="layout-panel__controls">
          {showTypograf && <TypografToggle />}
          {showLogoToggle && <LogoToggle />}
        </div>
        {showLogoToggle && <LogoHiddenWarning />}
      </>
    ) : null;
  const isAllProductsWhatsNewMode =
    dataChannel === "releases" &&
    activeSet === "whats-new" &&
    product.value === ALL_PRODUCT.value;

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("toolbar-collapsed", isCollapsed);
    return () => document.body.classList.remove("toolbar-collapsed");
  }, [isCollapsed]);

  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountRef = useRef(null);

  // The menu closes on an outside press and on Escape. Listeners are attached
  // only while it is open, so a closed menu costs nothing.
  useEffect(() => {
    if (!isAccountMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!accountRef.current?.contains(event.target)) setIsAccountMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  return (
    <>
    <aside
      className={cn({
        hidden: currentVideoPopup.length > 0 || isWrongBrowser,
        "toolbar-collapsed": isCollapsed,
      })}
    >
      <button
        className="toolbar-hide-btn"
        onClick={() => setIsCollapsed((c) => !c)}
        title={isCollapsed ? "Show toolbar" : "Hide toolbar"}
      >
        {isCollapsed ? "›" : "‹"}
      </button>
      <div className="toolbar-scroll">
        <div className="toolbar-inner">
          <div className="auth-status" ref={accountRef}>
            {authLoading && (
              <div className="account-skeleton" aria-hidden="true">
                <span className="account-skeleton-avatar" />
                <span className="account-skeleton-role" />
              </div>
            )}
            {!authLoading && user && (
              <>
                {/* The address itself is only ever a tooltip: at 370px it never
                    fits, and the role is the part that explains what the user
                    can see (getVisibleChannels, getManualLink). */}
                <button
                  type="button"
                  className={cn("account-trigger", {open: isAccountMenuOpen})}
                  title={user.email}
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  onClick={() => setIsAccountMenuOpen((open) => !open)}
                >
                  <span className="account-avatar">{initialsFromEmail(user.email)}</span>
                  <span className="account-role">{role}</span>
                  {isAccountMenuOpen ? <UpIcon size="xs" /> : <DownIcon size="xs" />}
                </button>

                {isAccountMenuOpen && (
                  <div className="account-menu" role="menu">
                    <div className="account-menu-head">
                      <div className="account-menu-email">{user.email}</div>
                      <div className="account-menu-role">Role: {role}</div>
                    </div>
                    <div className="account-menu-sep" />
                    {/* /scenes sits outside AppRoot, so an in-place navigation would
                        unmount the editor and discard unsaved work, whereas a new
                        tab keeps the editor mounted. Same for /admin. */}
                    <a
                      role="menuitem"
                      className="account-menu-item"
                      href="/scenes"
                      target="_blank"
                      rel="noopener"
                    >
                      <ProjectsIcon size="s" />
                      <span>My scenes</span>
                    </a>
                    {adminSections.length > 0 && (
                      <a
                        role="menuitem"
                        className="account-menu-item"
                        href={`/admin/${adminSections[0].path}`}
                        target="_blank"
                        rel="noopener"
                      >
                        <GearIcon size="s" />
                        <span>Admin console</span>
                      </a>
                    )}
                    <div className="account-menu-sep" />
                    <a
                      role="menuitem"
                      className="account-menu-item"
                      href={manualLink}
                      target="_blank"
                      rel="noopener"
                    >
                      <InfoIcon size="s" />
                      <span>Read the manual</span>
                    </a>
                    <a
                      role="menuitem"
                      className="account-menu-item"
                      href={feedbackLink}
                      target="_blank"
                      rel="noopener"
                    >
                      <MessageIcon size="s" />
                      <span>Leave your feedback</span>
                    </a>
                    <div className="account-menu-sep" />
                    <button
                      type="button"
                      role="menuitem"
                      className="account-menu-item"
                      onClick={handleLogout}
                    >
                      <ExitIcon size="s" />
                      <span>Log out</span>
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  className="home-button"
                  title="Home: open the default channel"
                  aria-label="Home"
                  onClick={onGoHome}
                >
                  <HomeIcon size="xs" />
                </button>
              </>
            )}
            {!authLoading && !user && (
              <a className="account-login" href="/auth/login">
                Log in
              </a>
            )}
          </div>
          <span className="label">Channel</span>
          <Select
            className="select"
            options={channelList}
            value={channel}
            onChange={(value) => onChangeChannel(value)}
            size="m"
            placeholder="Select template"
          />

          <span className="label">Product</span>
          <Select
            className="select"
            options={productList}
            value={product}
            onChange={(value) => onChangeProduct(value)}
            size="m"
            // The current product is not in the list when an admin hid it, so
            // count the products there are to switch to.
            isDisabled={!productList.some((p) => p.value !== product.value)}
            placeholder="Select product"
            isSearchable={true}
          />

          {channel.value === "livestream" && (
            <div className={cn("toolbar-row", textCn("rs-text-2"))}>
              <a
                className={textCn("rs-link", {external: true, mode: "standalone"})}
                href={PREPARE_PHOTO_LINK}
                target={"_blank"}
              >
                {"Prepare Photo"}
              </a>
            </div>
          )}

          {(typeList?.length > 1 || (channel.value === "ads" && typeList?.length > 0)) && (
            <>
              <span className="label">
                {channel.value === "releases" ? "Stage" : "Content type"}
              </span>
              <Select
                className="select"
                options={typeList}
                value={type}
                onChange={(value) => onChangeType(value)}
                size="m"
                placeholder={channel.value === "releases" ? "Select stage" : "Select type"}
              />
            </>
          )}

          {isSetsOffering && (
            <>
              <span className="label">Set</span>
              <Select
                className="select"
                options={listedSets}
                value={productSets.find((s) => s.value === activeSet) || defaultSet}
                onChange={(value) => {
                  const nextSet = value.value;
                  // Re-selecting the current set must not wipe the user's
                  // work — only an actual switch resets to the new defaults.
                  // No ?set= in the URL means the default set is active.
                  if (nextSet === (activeSet || defaultSet.value)) return;
                  clearSceneSet();

                  if (product.value === ALL_PRODUCT.value && nextSet !== "whats-new") {
                    // The channel's default product, or the first listed one
                    // when an admin hid it.
                    const fallbackProductValue = landingProduct(
                      DEFAULT_PRODUCTS_MAPPING[channel.value] || DEFAULT_PRODUCT.value,
                      productList.map((p) => p.value).filter((v) => v !== ALL_PRODUCT.value),
                      hiddenProducts
                    );
                    const fallbackTypeValue = type?.value || DEFAULT_TYPES[channel.value]?.value;
                    // `background` carries over the ALL-products view's own
                    // current choice; omitted (not forced to "1") when there
                    // isn't one, so the gallery's current first applies —
                    // see src/utils/url.js.
                    // A background that the new set hides gives way to the
                    // set's first one, as below.
                    const navParams = {layout: layoutOption || 1};
                    const targetHiddenBgs = hiddenBackgroundsOf(
                      releasesSettings,
                      homeType(fallbackTypeValue, fallbackProductValue),
                      fallbackProductValue,
                      nextSet
                    );
                    if (bgOption && isBackgroundOffered(String(bgOption), targetHiddenBgs)) {
                      navParams.background = bgOption;
                    }

                    if (nextSet !== defaultReleasesSetForProduct(fallbackProductValue).value) {
                      navParams.set = nextSet;
                    }

                    navigate(
                      buildUrl(channel.value, fallbackProductValue, fallbackTypeValue, navParams),
                      {replace: true, state: {resetOnArrival: true}}
                    );
                    return;
                  }

                  // Same rule as the stage/product switches: the new set
                  // renders exactly what a fresh page load of it would.
                  // The background stays when the new set offers its tile.
                  // One that the new set hides gives way to the set's first
                  // background, as on a bare page of the set.
                  const nextHiddenBgs = hiddenBackgroundsOf(
                    releasesSettings,
                    dataType,
                    product.value,
                    nextSet
                  );
                  const keptBackground =
                    urlBackground && isBackgroundOffered(urlBackground, nextHiddenBgs)
                      ? urlBackground
                      : null;

                  // A saved link (/scene/:id) shows one saved scene. Another
                  // set is another page: the editor leaves the link for the
                  // normal URL of the set, as the channel, product and stage
                  // switches do.
                  if (templateId) {
                    const navParams = {layout: layoutOption || 1};
                    if (keptBackground) navParams.background = keptBackground;
                    if (nextSet !== defaultSet.value) navParams.set = nextSet;
                    navigate(buildUrl(channel.value, product.value, type.value, navParams), {
                      state: {resetOnArrival: true},
                    });
                    return;
                  }

                  setSearchParams(
                    (params) => {
                      if (nextSet === defaultSet.value) {
                        params.delete("set");
                      } else {
                        params.set("set", nextSet);
                      }
                      // A number names a tile of the old set: the background
                      // goes by its identity, and the new set gives it its number.
                      if (namesUrlBackground(params)) {
                        writeUrlBackground(params, keptBackground, null);
                      }
                      return params;
                    },
                    {replace: true, state: {resetOnArrival: true}}
                  );
                }}
                size="m"
                placeholder="Select set"
              />
            </>
          )}

          {(dataChannel === "social" || dataChannel === "email") &&
            (features["sidebar.localization"] || isFontToggle) && (
            <div className="localization-wrap">
              <div className="localization">
                <span className="label">Localization</span>
                <Toggle
                  checked={isFontToggle}
                  className="font-toggle"
                  onChange={(e) => {
                    editIsFontToggle(e.target.checked);
                    setSearchParams(
                      (params) => {
                        if (e.target.checked) {
                          params.set("localization", fontOption);
                        } else {
                          params.delete("localization");
                        }
                        return params;
                      },
                      {replace: true}
                    );
                  }}
                ></Toggle>
              </div>

              {isFontToggle && <FontSelector />}
            </div>
          )}

          {channel.value === "ads" && type.value === "google-display-network" && (
            <div className="localization-wrap" data-testid="gdn-button-toggle">
              <div className="localization">
                <span className="label">Button</span>
                <Toggle
                  checked={isButtonEnabled}
                  className="font-toggle"
                  onChange={(e) => {
                    editIsButtonEnabled(e.target.checked);
                  }}
                />
              </div>
            </div>
          )}

          {channel.value === "livestream" && (
            <div className="localization-wrap">
              <div className="localization">
                <span className="label">Title Size</span>
                <div className="font-size-buttons">
                  <button
                    className={cn("font-size-btn", {active: titleFontSize === "default"})}
                    onClick={() => editTitleFontSize("default")}
                    title="Default font size"
                  >
                    T
                  </button>
                  <button
                    className={cn("font-size-btn font-size-btn--small", {
                      active: titleFontSize === "small",
                    })}
                    onClick={() => editTitleFontSize("small")}
                    title="Small font size (80%)"
                  >
                    <span style={{fontSize: "0.8em"}}>T</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {channel.value === "events" && (
            <>
              <span className="label">QR Code Links</span>
              <LinksPanel amount={eventLinksCount} />
            </>
          )}

          {showLayoutSelector && (
            <>
              <span className="label">Layout</span>
              <LayoutSelector
                options={offeredLayouts}
                iconChannel={layoutIconChannel}
                variants={productVariants}
                footer={layoutPanelFooter}
              />
            </>
          )}

          {/* Those controls ride in the layout panel's footer. When that panel
              is hidden they still have to reach the user, so they fall back to
              rows of their own. */}
          {!showLayoutSelector && layoutPanelFooter && (
            <div className="localization-wrap">{layoutPanelFooter}</div>
          )}

          {channel.value === "video" && (
            <>
              <span className="label">Uppercase</span>
              <div className="layout-uppercase">
                <Toggle
                  checked={isUppercase}
                  className="uppercase-toggle"
                  onChange={(e) => {
                    toggleUppercase(e.target.checked);
                  }}
                ></Toggle>
              </div>
            </>
          )}

          {channel.value === "social" && +layoutOption === 7 && (
            <>
              <span className="label">Icon</span>
              <IconSelector amount={iconAmount} />
            </>
          )}

          {!isAllProductsWhatsNewMode && (
            <>
              <ScopeBar />
              <span className="label">
                Background {backgroundsData.isPending ? "(Loading...)" : ""}
              </span>
              <BgSelector
                isLoading={backgroundsData.isPending || isLinkGalleryPending}
                amount={backgroundsData.backgrounds?.length}
                transforms={backgroundsData.transforms}
                allBgUrls={backgroundsData.backgrounds}
                displayOrder={bgDisplayOrder}
                linkGallery={linkGallery}
                linkBackground={linkBackground}
                isRouteDefaultReady={isRouteDefaultReady}
              />
              {/* Raster Livestream products never mount gradient controls.
                Other channels self-gate when no gradient SVG is active. */}
              {!(
                channel.value === "livestream" && isLivestreamRasterProduct(product.value)
              ) &&
                features["bg.gradient-stops"] && <GradientControls />}
              {/* Pixelation/mosaic overlay — available on every Releases set
                  (all Releases templates render PixelationLayer). Grouped
                  directly below the Background block. */}
              {dataChannel === "releases" &&
                allowsPixelation(product.value) &&
                (features["bg.pixelation"] || pixelationEnabled) && <PixelationControls />}
            </>
          )}

          {channel.value === "events" && (
            <>
              <span
                className={cn("label", "collapsible-label", {
                  open: isCustomQrOpen,
                  closed: !isCustomQrOpen,
                })}
                onClick={() => setIsCustomQrOpen((v) => !v)}
              >
                <span>Custom QR Code</span>
                <span>{isCustomQrOpen ? "▲" : "▼"}</span>
              </span>
              {isCustomQrOpen && (
                <div className="qr-selector">
                  {Array.from({length: eventLinksCount}, (_, i) => {
                    const slot = i + 1;
                    const inputId = `custom-qr-upload-${slot}`;
                    const imageKey = `custom-qr-${slot}`;
                    return (
                      <div className="custom-qr-upload" key={inputId}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCustomQRUploadFor(imageKey)}
                          style={{display: "none"}}
                          id={inputId}
                        />
                        <label htmlFor={inputId} className="bg-button upload-button">
                          <span>Upload QR {slot}</span>
                        </label>
                      </div>
                    );
                  })}
                  <div className={cn("qr-generator-wrapper", "toolbar-row", textCn("rs-text-2"))}>
                    <a
                      className={cn(
                        "qr-generator-link",
                        textCn("rs-link", {external: true, mode: "standalone"})
                      )}
                      href={"https://mari.europe-north1-gke.intellij.net/"}
                      target={"_blank"}
                      rel="noopener noreferrer"
                    >
                      {"Open QR Generator"}
                    </a>
                  </div>
                </div>
              )}
            </>
          )}

          {/*<div className="layout-uppercase">*/}
          {/*  <label className="label" htmlFor="blur">*/}
          {/*    Background Blur*/}
          {/*  </label>*/}
          {/*  <input*/}
          {/*    type="range"*/}
          {/*    min="0"*/}
          {/*    max="100"*/}
          {/*    value={bgBlurValue}*/}
          {/*    className="toolbar-slider"*/}
          {/*    id="blur"*/}
          {/*    onChange={(e) => {*/}
          {/*      editBgBlurValue(e.target.value);*/}
          {/*    }}*/}
          {/*  />*/}
          {/*</div>*/}

          {channel.value === "print" && (
            <>
              <span className="label">Data</span>
              <DataSelector />
            </>
          )}

          {(isToggleOnSocial ||
            isToggleOnInternal ||
            isToggleOnReleases ||
            channel.value === "livestream") && (
            <div className="localization-wrap" data-testid="dropzone-toggle">
              <div className="localization">
                <span className="label">{`Dropzone ${isDropzoneVisible ? "visible" : "hidden"}`}</span>
                <Toggle
                  checked={isDropzoneVisible}
                  className="font-toggle"
                  onChange={(e) => {
                    makeDropzoneVisible(e.target.checked);
                  }}
                ></Toggle>
              </div>
            </div>
          )}

          {/* "defaults.panel" requires editDefaultScenes (data/features.ts). */}
          {(features["defaults.panel"] || (user && gridMode !== "disabled")) && (
            <DefaultsMenu setLabel={activeSet || "default"} expanded={defaultsExpanded} onExpandedChange={setDefaultsExpanded}
              navigation={user && gridMode !== "disabled" && <GridModeNavigation />}>
              {features["defaults.panel"] && <DefaultScenePanel
                buildScene={buildScene}
                isChannelReady={isChannelReady}
                isRouteDefaultReady={isRouteDefaultReady}
                bgDisplayOrder={bgDisplayOrder}
                allBgUrls={backgroundsData.backgrounds}
                expanded={defaultsExpanded}
              />}
            </DefaultsMenu>
          )}

          <div className="toolbar-btns">
            {features["sidebar.copy-link"] && (
              <Button
                className="btn"
                onClick={saveSceneTrigger}
                size="m"
                mode="outline"
                id={"save_button"}
                disabled={!transformsReady}
              >
                <span>{copyButtonText}</span>
              </Button>
            )}

            <Button
              className="btn"
              onClick={handleExport}
              size="m"
              id={"export_button"}
              disabled={!transformsReady}
              mode="outline"
            >
              {isExportLoading ? <div className="loader"></div> : <span>Export</span>}
            </Button>
          </div>
          {channel.value === "ads" && type.value === "google-display-network" && (
            <div className="toolbar-btns toolbar-btns--preview">
              <Button
                className="btn"
                onClick={() => {
                  setSearchParams((params) => {
                    params.set("preview", "gdn");
                    return params;
                  });
                }}
                size="m"
                id="gdn_preview_button"
                mode="outline"
              >
                <span>Preview</span>
              </Button>
            </div>
          )}
          {/*{channel.value === "print" && (
          <div className="toolbar-btns">
            <Button className="btn" onClick={handlePDF} size="m" id={"pdf_button"} mode="outline">
              {isExportLoading ? <div className="loader"></div> : <span>Export PDF</span>}
            </Button>
          </div>
        )}*/}
        </div>
      </div>
    </aside>
    <ProjectsOverlay />
    </>
  );
};

export default Toolbar;
